/**
 * Home / Dashboard
 *
 * Layout (top to bottom):
 *   1. Header + Start Workout CTA (hero — always visible without scrolling)
 *   2. Injury exclusion chip (conditional)
 *   3. Interactive MuscleBodyMap with live fatigue colours
 *   4. Weekly stats + 7-day dots
 *   5. Streak / Consistency card
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { useState, useMemo } from 'react';
import { db } from '@/lib/db';
import { useNavigate } from 'react-router-dom';
import type { SplitDay, SplitType } from '@/types';
import MuscleBodyMap from '@/components/MuscleBodyMap';
import WeeklySetTargets from '@/components/WeeklySetTargets';

// ── Stat tile ─────────────────────────────────────────────────

function StatTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="flex-1 card-elevated text-center py-3">
      <p className="text-xl font-bold text-ink">{value}</p>
      <p className="text-xs text-ink-muted mt-0.5">{label}</p>
      {sub && <p className="text-xs text-ink-faint">{sub}</p>}
    </div>
  );
}

// ── 7-day training dots ───────────────────────────────────────

function WeekDots() {
  const trainedDays = useLiveQuery(async () => {
    const trained = new Set<string>();
    const cutoff = new Date(Date.now() - 7 * 86_400_000).toISOString();
    const sessions = await db.workouts
      .where('startedAt').aboveOrEqual(cutoff)
      .filter((w) => w.status === 'completed')
      .toArray();
    for (const s of sessions) trained.add(s.startedAt.slice(0, 10));
    return trained;
  }, []) ?? new Set<string>();

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    return {
      key: d.toISOString().slice(0, 10),
      label: d.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 1),
      isToday: i === 6,
    };
  });

  return (
    <div className="flex justify-between px-1">
      {days.map(({ key, label, isToday }) => {
        const trained = trainedDays.has(key);
        return (
          <div key={key} className="flex flex-col items-center gap-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center
                             text-xs font-semibold transition-colors
                             ${trained
                               ? 'bg-accent text-white'
                               : isToday
                               ? 'border border-accent/40 text-accent/60'
                               : 'bg-surface-300 text-ink-faint'}`}>
              {trained ? '✓' : label}
            </div>
            <span className={`text-xs ${isToday ? 'text-accent' : 'text-ink-faint'}`}>
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Streak / Consistency Card ─────────────────────────────────

function StreakCard() {
  const streaks = useLiveQuery(async () => {
    const sessions = await db.workouts
      .where('status').equals('completed')
      .sortBy('startedAt');

    const days = [...new Set(sessions.map((s) => s.startedAt.slice(0, 10)))].sort();
    if (days.length === 0) return { current: 0, best: 0, monthConsistency: 0 };

    let best = 1, run = 1;
    for (let i = 1; i < days.length; i++) {
      const gap = (new Date(days[i]).getTime() - new Date(days[i - 1]).getTime()) / 86_400_000;
      if (gap <= 2) { run++; best = Math.max(best, run); }
      else          { run = 1; }
    }

    const todayStr = new Date().toISOString().slice(0, 10);
    const yesterStr = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
    const lastDay = days[days.length - 1];
    let current = 0;
    if (lastDay === todayStr || lastDay === yesterStr) {
      current = 1;
      for (let i = days.length - 2; i >= 0; i--) {
        const gap = (new Date(days[i + 1]).getTime() - new Date(days[i]).getTime()) / 86_400_000;
        if (gap <= 2) current++;
        else break;
      }
    }

    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const daysElapsed = now.getDate();
    const monthDays = days.filter((d) => d >= monthStart).length;
    const monthConsistency = Math.round((monthDays / daysElapsed) * 100);

    return { current, best: Math.max(best, current), monthConsistency };
  }, []);

  if (!streaks || (streaks.current === 0 && streaks.best === 0)) return null;

  const flameColor = streaks.current >= 7 ? 'text-warning'
    : streaks.current >= 3 ? 'text-accent' : 'text-ink-muted';

  return (
    <div className="card space-y-3">
      <div className="flex gap-4 items-center">
        <div className="flex items-center gap-2">
          <span className={`text-2xl ${flameColor}`}>🔥</span>
          <div>
            <p className="text-xl font-bold text-ink leading-none">{streaks.current}</p>
            <p className="text-xs text-ink-muted">streak</p>
          </div>
        </div>
        <div className="w-px h-8 bg-surface-400" />
        <div>
          <p className="text-xl font-bold text-ink leading-none">{streaks.best}</p>
          <p className="text-xs text-ink-muted">best</p>
        </div>
        <div className="w-px h-8 bg-surface-400" />
        <div>
          <p className="text-xl font-bold text-ink leading-none">{streaks.monthConsistency}%</p>
          <p className="text-xs text-ink-muted">this month</p>
        </div>
      </div>
    </div>
  );
}

// ── Deload banner ─────────────────────────────────────────────

function DeloadBanner({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div className="card border border-warning/40 bg-warning/5 space-y-3 animate-fade-in">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xl">🔋</span>
          <div>
            <p className="font-semibold text-ink text-sm">Deload recommended</p>
            <p className="text-xs text-ink-muted mt-0.5">
              A muscle group has exceeded max recoverable volume for 2+ weeks.
              Your next session will use 50% weight and volume to let you recover.
            </p>
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="text-ink-faint text-sm flex-shrink-0 hover:text-ink-muted"
        >✕</button>
      </div>
    </div>
  );
}

// ── Injury warning chip ───────────────────────────────────────

function InjuryChip() {
  const navigate = useNavigate();
  const injuredCount = useLiveQuery(async () => {
    const states = await db.muscleStates.toArray();
    return states.filter((s) => s.isInjured).length;
  }, []) ?? 0;

  if (injuredCount === 0) return null;

  return (
    <button
      onClick={() => navigate('/settings')}
      className="flex items-center gap-2 px-3 py-2 rounded-xl
                 bg-warning/10 border border-warning/30 text-warning text-sm
                 w-full text-left hover:bg-warning/15 transition-colors active:scale-[0.99]"
    >
      <span>⚠</span>
      <span className="flex-1">
        {injuredCount} muscle{injuredCount > 1 ? 's' : ''} excluded from generation
      </span>
      <span className="text-xs text-warning/70">Manage →</span>
    </button>
  );
}

// ── Main page ─────────────────────────────────────────────────

const SPLIT_LABELS: Record<string, { label: string; muscles: string }> = {
  push:          { label: 'Push Day',   muscles: 'Chest · Delts · Triceps' },
  anterior:      { label: 'Push Day',   muscles: 'Chest · Delts · Triceps' },
  pull:          { label: 'Pull Day',   muscles: 'Back · Rear Delts · Biceps' },
  posterior:     { label: 'Pull Day',   muscles: 'Back · Rear Delts · Biceps' },
  legs:          { label: 'Leg Day',    muscles: 'Quads · Hamstrings · Glutes · Calves' },
  upper:         { label: 'Upper Body', muscles: 'Chest · Back · Shoulders · Arms' },
  lower:         { label: 'Lower Body', muscles: 'Quads · Hamstrings · Glutes · Core' },
  full_body_day: { label: 'Full Body',  muscles: 'Chest · Back · Legs · Shoulders' },
};

// Extra muscles shown when there's no dedicated leg day
const PUSH_PULL_EXTRAS: Partial<Record<SplitDay, string>> = {
  push: ' · Quads',
  anterior: ' · Quads',
  pull: ' · Hamstrings · Glutes',
  posterior: ' · Hamstrings · Glutes',
};

const SPLIT_DAY_OPTIONS: Record<SplitType, SplitDay[]> = {
  push_pull:      ['push', 'pull'],
  push_pull_legs: ['push', 'pull', 'legs'],
  upper_lower:    ['upper', 'lower'],
  full_body:      ['full_body_day'],
};

const DAY_SHORT_LABEL: Record<string, string> = {
  push: 'Push', pull: 'Pull', legs: 'Legs',
  upper: 'Upper', lower: 'Lower', full_body_day: 'Full Body',
};

export default function HomePage() {
  const navigate = useNavigate();
  const [deloadDismissed, setDeloadDismissed] = useState(false);
  const [dayOverride, setDayOverride] = useState<SplitDay | null>(null);
  const [durationOverride, setDurationOverride] = useState<number | null>(null);

  const settings = useLiveQuery(() => db.settings.get('singleton'), []);
  const nextSplitDay = settings?.nextSplitDay ?? 'push';
  const splitType = (settings?.splitType ?? 'push_pull') as SplitType;
  const selectedDay = (dayOverride ?? nextSplitDay) as SplitDay;
  const hasDedicatedLegDay = splitType === 'push_pull_legs' || splitType === 'upper_lower';
  const nextInfo = useMemo(() => {
    const base = SPLIT_LABELS[selectedDay] ?? SPLIT_LABELS['push'];
    if (hasDedicatedLegDay) return base;
    const extra = PUSH_PULL_EXTRAS[selectedDay] ?? '';
    return { ...base, muscles: base.muscles + extra };
  }, [selectedDay, hasDedicatedLegDay]);
  const dayOptions = useMemo(() => SPLIT_DAY_OPTIONS[splitType] ?? ['push', 'pull'], [splitType]);

  const recentWorkoutCount = useLiveQuery(async () => {
    const cutoff = new Date(Date.now() - 7 * 86_400_000).toISOString();
    return db.workouts
      .where('startedAt').aboveOrEqual(cutoff)
      .filter((w) => w.status === 'completed')
      .count();
  }, []);

  const totalWeeklyHardSets = useLiveQuery(async () => {
    const cutoff = new Date(Date.now() - 7 * 86_400_000).toISOString();
    return db.loggedSets
      .where('loggedAt').aboveOrEqual(cutoff)
      .filter((s) => !s.isWarmup && s.rpe >= 7)
      .count();
  }, []);

  const totalWorkouts = useLiveQuery(
    () => db.workouts.where('status').equals('completed').count(), []
  );

  const hasWorkouts = (totalWorkouts ?? 0) > 0;

  return (
    <div className="content-pad-bottom px-4 pt-6 pb-4 space-y-5 animate-fade-in">

      {/* Header */}
      <header>
        <p className="text-ink-muted text-sm">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </p>
        <h1 className="text-2xl font-bold text-ink tracking-tight">Dashboard</h1>
      </header>

      {/* ── HERO: Start Workout CTA ───────────────────────────── */}
      <div className="card-elevated rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between gap-4 px-4 py-4">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-ink-muted font-medium mb-1">Next up</p>
            <p className="text-xl font-bold text-ink">{nextInfo.label}</p>
            <p className="text-xs text-ink-muted mt-0.5 truncate">{nextInfo.muscles}</p>
          </div>
          <button
            className="btn-primary whitespace-nowrap text-base px-6 py-3.5 rounded-xl"
            onClick={() => navigate('/workout', {
              state: (dayOverride || durationOverride) ? {
                ...(dayOverride      && { splitDayOverride: dayOverride }),
                ...(durationOverride && { durationOverride }),
              } : undefined,
            })}
          >
            Start
          </button>
        </div>
        {/* Day-picker chips — only shown when there are multiple options */}
        {dayOptions.length > 1 && (
          <div className="flex gap-2 px-4 pb-1">
            {dayOptions.map((day) => (
              <button
                key={day}
                onClick={() => setDayOverride(day === nextSplitDay && dayOverride === null ? null : day)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors active:scale-95 ${
                  selectedDay === day
                    ? 'bg-accent text-white'
                    : 'bg-surface-300 text-ink-muted border border-surface-400 hover:bg-surface-400'
                }`}
              >
                {DAY_SHORT_LABEL[day] ?? day}
              </button>
            ))}
          </div>
        )}
        {/* Duration chips */}
        <div className="flex gap-2 px-4 pb-3">
          {[30, 45, 60, 75].map((mins) => {
            const savedMins = settings?.preferredDurationMinutes ?? 60;
            const isActive = durationOverride === mins || (durationOverride === null && savedMins === mins);
            return (
              <button
                key={mins}
                onClick={() => setDurationOverride(mins === savedMins && durationOverride === null ? null : mins)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors active:scale-95 ${
                  isActive
                    ? 'bg-accent text-white'
                    : 'bg-surface-300 text-ink-muted border border-surface-400 hover:bg-surface-400'
                }`}
              >
                {mins}m
              </button>
            );
          })}
        </div>
        {/* Accent bar */}
        <div className="h-1 bg-accent/30 w-full" />
      </div>

      {/* Deload banner */}
      {settings?.deloadRecommended && !deloadDismissed && (
        <DeloadBanner onDismiss={() => setDeloadDismissed(true)} />
      )}

      {/* Injury exclusion chip */}
      <InjuryChip />

      {/* Body map legend (first time only) */}
      {!hasWorkouts && (
        <div className="card border border-accent/20 space-y-3 animate-fade-in">
          <p className="font-semibold text-ink text-sm">How the Body Map Works</p>
          <div className="space-y-2 text-xs text-ink-muted leading-relaxed">
            <div className="flex gap-2 items-center">
              <span className="w-3 h-3 rounded-full bg-fresh flex-shrink-0" />
              <span><span className="text-fresh font-semibold">Green</span> — fresh, ready to train</span>
            </div>
            <div className="flex gap-2 items-center">
              <span className="w-3 h-3 rounded-full bg-partial flex-shrink-0" />
              <span><span className="text-partial font-semibold">Amber</span> — partially recovered</span>
            </div>
            <div className="flex gap-2 items-center">
              <span className="w-3 h-3 rounded-full bg-fatigued flex-shrink-0" />
              <span><span className="text-fatigued font-semibold">Red</span> — fatigued, generator will skip</span>
            </div>
          </div>
        </div>
      )}

      {/* Weekly Set Targets — Fitbod-style volume tracker */}
      <WeeklySetTargets />

      {/* Interactive muscle body map */}
      <MuscleBodyMap />

      {/* Weekly stats */}
      <div className="flex gap-3">
        <StatTile label="Sessions"  value={String(recentWorkoutCount ?? 0)} sub="this week" />
        <StatTile label="Hard Sets" value={String(totalWeeklyHardSets ?? 0)} sub="this week" />
        <StatTile label="All-Time"  value={String(totalWorkouts ?? 0)} sub="workouts" />
      </div>

      {/* 7-day training dots */}
      <div className="card space-y-3">
        <p className="text-sm font-semibold text-ink-muted">This Week</p>
        <WeekDots />
      </div>

      {/* Streak / consistency */}
      {hasWorkouts && <StreakCard />}

    </div>
  );
}
